import { RefObject, useEffect, useLayoutEffect, useState } from "react";

import {
    FeaturesValuePropBlock,
    ImageChooserBlock,
    JSONCharBlock,
} from "@reactivated";

import {
    ValuePropAdjustment,
    baseAdjustment,
    calculateBodyHideAdjustments,
    calculateCollisionAdjustments,
    calculateFinalRect,
    calculateFlipAdjustments,
    detectCollisions,
} from "./utils";

export const useImageBounds = (
    imageWrapperRef: RefObject<HTMLElement | null>,
    innerRef: RefObject<HTMLElement | null>,
    hotspotImage: ImageChooserBlock | null,
) => {
    const [imageBounds, setImageBounds] = useState<DOMRect | null>(null);

    useEffect(() => {
        const handleResize = () => {
            const image = imageWrapperRef.current;
            const inner = innerRef.current;

            if (image && inner) {
                const imageRect = image.getBoundingClientRect();
                const innerRect = inner.getBoundingClientRect();
                const aspectRatio = hotspotImage
                    ? hotspotImage.width / hotspotImage.height
                    : 1;

                const top =
                    aspectRatio > 1
                        ? (imageRect.height - imageRect.height / aspectRatio) /
                          2
                        : 0;
                const height =
                    aspectRatio > 1
                        ? imageRect.height / aspectRatio
                        : imageRect.height;
                const left =
                    aspectRatio < 1
                        ? imageRect.left +
                          (imageRect.width - imageRect.width * aspectRatio) /
                              2 -
                          innerRect.left
                        : imageRect.left - innerRect.left;
                const width =
                    aspectRatio < 1
                        ? imageRect.width * aspectRatio
                        : imageRect.width;

                setImageBounds(new DOMRect(left, top, width, height));
            }
        };

        handleResize();

        // Feels super hacky but seems to be necessary to handle the tablet layout
        requestAnimationFrame(() => {
            handleResize();
        });

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    return imageBounds;
};

/**
 * I avoided adding this for a long time because I didn't want the collision/adjustment
 * logic to be too dependent on FeaturesBlock in particular. But for the body hide case,
 * it makes sense to use because we can pretty easily figure out if there's going to be
 * a problem ahead of time if we know the alignment of the hotspots/value props.
 */
export type HotspotWithAlignment = JSONCharBlock[number] & {
    alignment: FeaturesValuePropBlock["alignment"];
};

/**
 * Hook for calculating value prop adjustments based on container bounds and collisions
 */
export function useValuePropAdjustments(
    valuePropRefs: React.RefObject<(HTMLDivElement | null)[]>,
    innerRef: React.RefObject<HTMLDivElement | null>,
    imageBounds: DOMRect | null,
    hotspots: HotspotWithAlignment[] | null,
) {
    const [adjustments, setAdjustments] = useState<ValuePropAdjustment[]>([]);

    useLayoutEffect(() => {
        const containerRect = innerRef.current?.getBoundingClientRect();

        // Do a bunch of null checking before we begin
        if (!containerRect || !imageBounds || !hotspots) return;
        if (valuePropRefs.current.some((ref) => ref === null)) return;
        const refs = valuePropRefs as React.RefObject<HTMLDivElement[]>;

        // Start with an array of base adjustments
        let adjustments: ValuePropAdjustment[] = hotspots.map((_) => ({
            ...baseAdjustment,
        }));

        // Then, figure out which props will be truncated because of their height
        adjustments = calculateBodyHideAdjustments(
            adjustments,
            containerRect,
            refs,
            hotspots,
        );

        // Then, calculate flip adjustments
        adjustments = calculateFlipAdjustments(
            adjustments,
            containerRect,
            refs,
        );

        // Now that we have preemptively marked which adjustments need special
        // treatment, calculate rects for collision detection
        const rects = refs.current.map((element, index) => {
            const adjustment = adjustments[index] || baseAdjustment;
            return calculateFinalRect(element, adjustment);
        });

        // Next, do the actual collision detection
        const collisions = detectCollisions(rects);

        // Finally, apply adjustments based on collision data
        adjustments = calculateCollisionAdjustments(
            adjustments,
            collisions,
            rects,
            containerRect,
            hotspots,
        );

        setAdjustments(adjustments);
    }, [imageBounds]);

    return adjustments;
}
